import React, { useState, useMemo } from 'react';
import { Input, Button, Select, Space, Typography, Spin, message, Tooltip, InputNumber, Slider } from 'antd';
import {
    SendOutlined, LoadingOutlined, SettingOutlined, PictureOutlined,
    ExpandAltOutlined, DownOutlined, UpOutlined, PlusOutlined, SearchOutlined
} from '@ant-design/icons';
import { PinterestOutlined } from '@ant-design/icons';
import { useGeneration } from '../../../context/GenerationContext';
import { useQueryClient } from '@tanstack/react-query';
import { useCanvasGeneratedMedia, canvasKeys } from '../../../api/canvasApi';
import api from '../../../api/client';

const { Text } = Typography;

const PinterestPanel = ({ canvasId, onMediaClick, collapsed, onToggleCollapse, onAddToCanvas }) => {
    const queryClient = useQueryClient();
    const { trackGeneration } = useGeneration();
    const [keyword, setKeyword] = useState('');
    const [searching, setSearching] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [maxPins, setMaxPins] = useState(25);
    const [selectedImage, setSelectedImage] = useState(null);

    const { data: media = [], isLoading: mediaLoading } = useCanvasGeneratedMedia(canvasId);

    // Filter for Pinterest results
    const pinterestResults = useMemo(() => {
        return media
            .filter(item => item.provider === 'pinterest' || (item.metadata && item.metadata.provider === 'pinterest'))
            .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
    }, [media]);

    const handleSearch = async () => {
        if (!keyword.trim()) {
            message.warning('Please enter a search term');
            return;
        }

        setSearching(true);
        try {
            console.log('Pinterest Search:', keyword, 'max:', maxPins);

            const response = await api.post('/pinterest/search', {
                keyword: keyword.trim(),
                max_pins: maxPins,
                canvas_id: canvasId
            });

            const result = response.data;

            if (result && result.job_id) {
                message.success('Pinterest search started!');

                // Track for polling
                trackGeneration(result.job_id, 'pinterest', { keyword, provider: 'pinterest', maxPins }, {
                    onComplete: () => {
                        queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
                        message.success('Pinterest images ready!');
                    }
                });

                // Refresh to show IN_QUEUE placeholder
                queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
            } else {
                message.error('Failed to start Pinterest search');
            }

        } catch (error) {
            console.error('Pinterest search failed:', error);
            message.error('Search failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setSearching(false);
        }
    };

    return (
        <div style={{
            position: 'relative',
            height: collapsed ? 'auto' : '100%',
        }}>
            {/* Floating Toggle Button - Aligned with header when collapsed, bottom right when open */}
            <div style={{
                position: 'absolute',
                top: collapsed ? 10 : 'auto',
                bottom: collapsed ? 'auto' : 8,
                right: 8,
                zIndex: 10
            }}>
                <Button
                    type="text"
                    icon={collapsed ? <UpOutlined /> : <DownOutlined />}
                    size="small"
                    onClick={onToggleCollapse}
                    style={{
                        color: '#8b949e',
                        background: 'rgba(22, 27, 34, 0.95)',
                        border: '1px solid #30363d',
                        borderRadius: 6
                    }}
                />
            </div>

            {/* Main Panel Card */}
            <div style={{
                background: 'rgba(22, 27, 34, 0.95)',
                border: '1px solid #30363d',
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'column',
                height: collapsed ? 'auto' : '100%',
                overflow: 'hidden',
                backdropFilter: 'blur(8px)',
                position: 'relative',
                zIndex: 2
            }}>
                {/* Header */}
                <div style={{
                    padding: '12px 16px',
                    borderBottom: collapsed ? 'none' : '1px solid #30363d',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.02)'
                }}>
                    <Space>
                        <PinterestOutlined style={{ color: '#E60023' }} />
                        <Text style={{ color: '#fff', fontWeight: 600 }}>Pinterest</Text>
                    </Space>
                    {!collapsed && (
                        <Button
                            type="text"
                            icon={<SettingOutlined />}
                            size="small"
                            onClick={() => setSettingsOpen(!settingsOpen)}
                            style={{ color: settingsOpen ? '#E60023' : '#8b949e' }}
                        />
                    )}
                </div>

                {!collapsed && (
                    <>
                        {/* Search Area */}
                        <div style={{ padding: 16, borderBottom: '1px solid #30363d' }}>
                            <div style={{ position: 'relative' }}>
                                <Input
                                    placeholder="Search Pinterest for inspiration..."
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                    onPressEnter={handleSearch}
                                    prefix={<SearchOutlined style={{ color: '#8b949e' }} />}
                                    suffix={
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={searching ? <LoadingOutlined /> : <SendOutlined />}
                                            onClick={handleSearch}
                                            disabled={searching || !keyword.trim()}
                                            style={{ background: '#E60023', borderColor: '#E60023' }}
                                        />
                                    }
                                    style={{
                                        background: '#0d1117',
                                        border: '1px solid #30363d',
                                        borderRadius: 8,
                                        color: '#fff',
                                    }}
                                />
                            </div>

                            {/* Settings Panel */}
                            {settingsOpen && (
                                <div style={{ marginTop: 12, padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
                                    <div>
                                        <Text style={{ color: '#8b949e', fontSize: 10, display: 'block', marginBottom: 4 }}>
                                            MAX RESULTS: {maxPins}
                                        </Text>
                                        <Slider
                                            min={5}
                                            max={50}
                                            value={maxPins}
                                            onChange={setMaxPins}
                                            marks={{ 5: '5', 25: '25', 50: '50' }}
                                            trackStyle={{ background: '#E60023' }}
                                            handleStyle={{ borderColor: '#E60023' }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Main Image View */}
                        <div style={{
                            flex: 1,
                            background: '#010409',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            {selectedImage ? (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                    <img
                                        src={selectedImage.url}
                                        alt={selectedImage.title || 'Pinterest image'}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />

                                    {/* Top Left: Add to Canvas */}
                                    <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}>
                                        <Tooltip title="Add to Canvas">
                                            <Button
                                                size="small"
                                                shape="circle"
                                                icon={<PlusOutlined style={{ color: '#fff' }} />}
                                                style={{
                                                    background: 'rgba(0,0,0,0.5)',
                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                    backdropFilter: 'blur(4px)'
                                                }}
                                                onClick={() => {
                                                    if (onAddToCanvas && selectedImage) {
                                                        onAddToCanvas({
                                                            src: selectedImage.url,
                                                            label: selectedImage.title || keyword,
                                                            title: selectedImage.title
                                                        });
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    </div>

                                    {/* Top Right: Expand */}
                                    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
                                        <Tooltip title="Expand Preview">
                                            <Button
                                                size="small"
                                                shape="circle"
                                                icon={<ExpandAltOutlined style={{ color: '#fff' }} />}
                                                style={{
                                                    background: 'rgba(0,0,0,0.5)',
                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                    backdropFilter: 'blur(4px)'
                                                }}
                                                onClick={() => {
                                                    if (onMediaClick && selectedImage) {
                                                        const index = pinterestResults.findIndex(item => item.id === selectedImage.id);
                                                        if (index !== -1) {
                                                            const formattedList = pinterestResults.map(g => ({
                                                                ...g,
                                                                src: g.url,
                                                                label: g.metadata?.title || g.prompt,
                                                                type: 'image',
                                                                createdAt: g.created_at || g.createdAt
                                                            }));
                                                            onMediaClick(index, formattedList);
                                                        }
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    </div>

                                    {/* Bottom: Title Overlay */}
                                    {selectedImage.metadata?.title && (
                                        <div style={{
                                            position: 'absolute',
                                            bottom: 0, left: 0, right: 0,
                                            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)',
                                            padding: '20px 16px 12px',
                                        }}>
                                            <Text style={{ color: '#fff', fontSize: 12, lineHeight: 1.4 }}>
                                                {selectedImage.metadata.title}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#484f58', padding: 20 }}>
                                    <PictureOutlined style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }} />
                                    <Text style={{ color: '#484f58', display: 'block' }}>Search Pinterest for inspiration</Text>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* External Floating Thumbnails Strip */}
            {!collapsed && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '100%',
                    marginLeft: 12,
                    width: 130,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    paddingBottom: 12,
                    pointerEvents: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }} className="pinterest-thumbnails-strip">
                    <style>{`
                        .pinterest-thumbnails-strip::-webkit-scrollbar { display: none; }
                    `}</style>

                    {pinterestResults.flatMap((item) => {
                        // Show loading placeholder for IN_QUEUE items
                        if (item.status === 'IN_QUEUE' || item.status === 'IN_PROGRESS') {
                            // Use maxPins from metadata, or default to current maxPins setting
                            const placeholderCount = item.metadata?.max_pins || item.metadata?.maxPins || maxPins;
                            return Array.from({ length: placeholderCount }).map((_, idx) => (
                                <div
                                    key={`${item.id}_loading_${idx}`}
                                    style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: 8,
                                        border: '2px solid rgba(230, 0, 35, 0.3)',
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                        background: '#0d1117',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: 8
                                    }}
                                >
                                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#E60023' }} spin />} />
                                </div>
                            ));
                        }

                        // Normal completed item
                        return (
                            <div
                                key={item.id}
                                onClick={() => setSelectedImage(item)}
                                style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 8,
                                    border: selectedImage?.id === item.id ? '2px solid #E60023' : '2px solid rgba(48, 54, 61, 0.4)',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    background: '#0d1117',
                                    opacity: selectedImage?.id === item.id ? 1 : 0.7,
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                    marginBottom: 8
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                onMouseLeave={(e) => { if (selectedImage?.id !== item.id) e.currentTarget.style.opacity = 0.7 }}
                            >
                                <img src={item.url} alt="thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                        );
                    })}

                    {pinterestResults.length === 0 && (
                        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 10 }}>
                            No Results
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PinterestPanel;
